import { useCallback, useState } from 'react';
import {
  useCompleteUploadMutation,
  useInitUploadBatchMutation,
  useInitUploadMutation,
} from '@/state/slices/uploads';
import {
  browserUploads,
  type SendProgress,
} from '@/features/uploads/utils/browserUploads';
import { classifySelection, relativePathOf } from '../utils/selection';

/**
 * The hook's state covers only the request for upload URLs. Once they are
 * granted the dialog closes and the Uploads page follows the transfer.
 */
export type UploadStage = 'idle' | 'requesting-url' | 'error';

export interface UploadState {
  stage: UploadStage;
  errorMessage?: string;
}

interface UploadArgs {
  files: File[];
  /**
   * Called once the server has granted the upload, before any byte is sent.
   * From then on the upload is followed on the Uploads page (via
   * `browserUploads`), not through this hook's state, so the dialog can close.
   */
  onStarted: (uploadId: string) => void;
}

export interface UploadResult {
  uploadId: string;
  /** Files that never reached GCS after every retry. 0 on a clean upload. */
  failedCount: number;
}

/**
 * Browsers cap HTTP/1.1 connections per host at about six. Going higher only
 * queues inside the browser.
 */
const MAX_CONCURRENT_PUTS = 6;

/** Four attempts total per file (specs/uploads/AP-865-folder-upload-client.md §5). */
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 500;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const putToSignedUrl = (url: string, file: File): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new Error(
            `GCS upload failed (${xhr.status}): ${xhr.responseText || xhr.statusText}`
          )
        );
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new Error('Upload aborted'));
    xhr.send(file);
  });

/**
 * PUT one file, retrying its own transfer. Resolves true when the bytes landed,
 * false once the attempts are spent — a single file is not allowed to fail the
 * whole study.
 */
async function putWithRetry(url: string, file: File): Promise<boolean> {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      // A retry exists only because the previous attempt failed.
      // eslint-disable-next-line no-await-in-loop
      await putToSignedUrl(url, file);
      return true;
    } catch {
      if (attempt === MAX_RETRIES) return false;
      // eslint-disable-next-line no-await-in-loop -- backoff between attempts.
      await sleep(RETRY_DELAY_MS);
    }
  }
  return false;
}

/** Run `tasks` with at most `limit` in flight, preserving nothing but the count. */
async function runPool<T>(
  tasks: (() => Promise<T>)[],
  limit: number
): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let next = 0;
  const workers = Array.from(
    { length: Math.min(limit, tasks.length) },
    async () => {
      while (next < tasks.length) {
        const index = next;
        next += 1;

        // This worker IS the concurrency limit: starting the next task
        // before this one settles is exactly what the cap forbids.
        // eslint-disable-next-line no-await-in-loop
        results[index] = await tasks[index]();
      }
    }
  );
  await Promise.all(workers);
  return results;
}

/**
 * Upload orchestration (AP-865).
 *
 *   A single pre-made .zip → POST /uploads/init/ then one PUT. Unchanged.
 *   Anything else           → POST /uploads/init/batch/ then one PUT per file,
 *                             six at a time, each retried on its own.
 *
 * The bytes never proxy through ai-pacs-be; it only brokers the signed URLs,
 * holding the ingestion API key server-side.
 */
export function useUploadStudy() {
  const [state, setState] = useState<UploadState>({ stage: 'idle' });
  const [initUpload] = useInitUploadMutation();
  const [initUploadBatch] = useInitUploadBatchMutation();
  const [completeUpload] = useCompleteUploadMutation();

  /**
   * POST uploads/<id>/complete/ once every PUT has settled (AP-899 R15). For a
   * folder it names the files that never reached GCS, so ingestion drops them
   * from the total instead of waiting on them forever. Never fails the upload:
   * the bytes that landed are processed either way.
   */
  const reportComplete = useCallback(
    async (uploadId: string, failedOrdinals: number[] = []) => {
      try {
        await completeUpload(
          failedOrdinals.length ? { uploadId, failedOrdinals } : { uploadId }
        ).unwrap();
      } catch {
        // Swallowed on purpose.
      }
    },
    [completeUpload]
  );

  const upload = useCallback(
    async ({ files, onStarted }: UploadArgs): Promise<UploadResult> => {
      // Once started, this hook's state stops following the upload: the
      // dialog is free to close or start another one.
      let sendingId: string | undefined;
      const begin = (uploadId: string, initial: SendProgress) => {
        sendingId = uploadId;
        browserUploads.start(uploadId, initial);
        setState({ stage: 'idle' });
        onStarted(uploadId);
      };

      setState({ stage: 'requesting-url' });
      try {
        const selection = classifySelection(files);

        if (selection.kind === 'archive') {
          const init = await initUpload({
            filename: selection.file.name,
          }).unwrap();

          begin(init.uploadId, { filesSent: 0, filesTotal: 1 });
          const sent = await putWithRetry(init.uploadUrl, selection.file);
          browserUploads.progress(init.uploadId, {
            filesSent: 1,
            filesTotal: 1,
          });
          if (!sent) throw new Error('Upload failed');
          await reportComplete(init.uploadId);
          return { uploadId: init.uploadId, failedCount: 0 };
        }

        const init = await initUploadBatch({
          files: selection.files.map((file) => ({
            relativePath: relativePathOf(file),
          })),
        }).unwrap();

        // The server decides order and which paths get a URL, so pair its items
        // back to our File objects by path. An item we cannot match is skipped
        // rather than filled with the wrong bytes.
        const byPath = new Map(
          selection.files.map((file) => [relativePathOf(file), file])
        );
        const slots = init.items
          .map((item) => ({ item, file: byPath.get(item.relativePath) }))
          .filter(
            (slot): slot is { item: typeof slot.item; file: File } =>
              slot.file !== undefined
          );

        if (slots.length === 0) {
          throw new Error('No files matched the granted upload URLs');
        }

        // The page counts files: one more each time a file's PUT settles.
        let filesSent = 0;
        begin(init.uploadId, { filesSent, filesTotal: slots.length });
        const outcomes = await runPool(
          slots.map((slot) => async () => {
            const ok = await putWithRetry(slot.item.uploadUrl, slot.file);
            filesSent += 1;
            browserUploads.progress(init.uploadId, {
              filesSent,
              filesTotal: slots.length,
            });
            return ok;
          }),
          MAX_CONCURRENT_PUTS
        );

        // Every granted ordinal that never landed: PUTs that gave up, and
        // items we could not pair with a picked file (never sent at all).
        const sentOrdinals = new Set(
          slots.filter((_, index) => outcomes[index]).map((s) => s.item.ordinal)
        );
        const failedOrdinals = init.items
          .map((item) => item.ordinal)
          .filter((ordinal) => !sentOrdinals.has(ordinal));
        await reportComplete(init.uploadId, failedOrdinals);

        const failedCount = outcomes.filter((ok) => !ok).length;
        if (failedCount === outcomes.length) {
          throw new Error(`No files could be uploaded (${failedCount} failed)`);
        }

        return { uploadId: init.uploadId, failedCount };
      } catch (err) {
        // RTK Query mutation errors come through as { status, data, error? }.
        const rtkErr = err as {
          status?: number | string;
          data?: { detail?: string; error?: string };
          error?: string;
        };
        const message =
          (err instanceof Error && err.message) ||
          rtkErr?.data?.detail ||
          rtkErr?.data?.error ||
          rtkErr?.error ||
          'Upload failed';
        // After the start the dialog is gone; the caller reports the failure.
        if (!sendingId) setState({ stage: 'error', errorMessage: message });
        throw err;
      } finally {
        if (sendingId) browserUploads.finish(sendingId);
      }
    },
    [initUpload, initUploadBatch, reportComplete]
  );

  const reset = useCallback(() => setState({ stage: 'idle' }), []);

  return { upload, reset, state };
}
